import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { LikeOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Table, Tooltip, Typography, message } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { useStore } from 'effector-react'

import { $userRole } from '../../features/user'
import { role } from '../../libs'

import { changeIdeaStatus, getIdeas, voteForIdea } from './api'
import { Idea } from './types'

const PAGE_SIZE = 20

const isInternalCrmPath = (sourcePage: string) =>
  sourcePage.startsWith('/') && !sourcePage.startsWith('//')

const formatDate = (value: string) => {
  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

export const IdeasPage = () => {
  const [ideas, setIdeas] = useState<Idea[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [votingId, setVotingId] = useState<string | null>(null)
  const [statusChangingId, setStatusChangingId] = useState<string | null>(null)
  const userRole = useStore($userRole)
  const canManageIdeaStatus = userRole?.includes(role.it.crmDeveloper()) ?? false

  const loadIdeas = useCallback(async () => {
    setLoading(true)
    setError(false)

    try {
      const response = await getIdeas(page, PAGE_SIZE)
      setIdeas(response.items)
      setTotal(response.total)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => {
    loadIdeas()
  }, [loadIdeas])

  const like = async (idea: Idea) => {
    if (idea.status === 'closed') return

    setVotingId(idea.id)

    try {
      const updatedIdea = await voteForIdea(
        idea.id,
        idea.currentVote === 'like' ? null : 'like',
      )
      setIdeas((items) =>
        items.map((item) => (item.id === updatedIdea.id ? updatedIdea : item)),
      )
    } catch {
      message.error('Could not save your vote. Please try again.')
    } finally {
      setVotingId(null)
    }
  }

  const changeStatus = async (idea: Idea) => {
    const status = idea.status === 'open' ? 'closed' : 'open'

    setStatusChangingId(idea.id)

    try {
      const updatedIdea = await changeIdeaStatus(idea.id, status)
      setIdeas((items) =>
        items.map((item) => (item.id === updatedIdea.id ? updatedIdea : item)),
      )
    } catch {
      message.error('Could not update the idea status. Please try again.')
    } finally {
      setStatusChangingId(null)
    }
  }

  const columns: ColumnsType<Idea> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 100,
      sorter: (left, right) => Number(left.id) - Number(right.id),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Author email',
      dataIndex: 'authorEmail',
      width: 230,
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      width: 180,
      render: formatDate,
    },
    {
      title: 'Source page',
      dataIndex: 'sourcePage',
      width: 220,
      render: (sourcePage: string) =>
        isInternalCrmPath(sourcePage) ? (
          <Link to={sourcePage}>
            <Typography.Text code>{sourcePage}</Typography.Text>
          </Link>
        ) : (
          <Typography.Text code>{sourcePage}</Typography.Text>
        ),
    },
    {
      title: 'Idea',
      dataIndex: 'text',
      render: (text: string) => (
        <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>{text}</Typography.Text>
      ),
    },
    {
      title: 'Likes',
      key: 'likes',
      width: 100,
      sorter: (left, right) => left.likes - right.likes,
      sortDirections: ['ascend', 'descend'],
      render: (_, idea) => (
        <Tooltip title={idea.status === 'closed' ? 'Voting is unavailable for closed ideas' : 'Like'}>
          <Button
            aria-label="Like idea"
            disabled={idea.status === 'closed'}
            icon={<LikeOutlined />}
            loading={votingId === idea.id}
            size="small"
            type={idea.currentVote === 'like' ? 'primary' : 'text'}
            onClick={() => like(idea)}
          >
            {idea.likes}
          </Button>
        </Tooltip>
      ),
    },
    ...(canManageIdeaStatus
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: 130,
            render: (_: unknown, idea: Idea) => (
              <Button
                loading={statusChangingId === idea.id}
                size="small"
                onClick={() => changeStatus(idea)}
              >
                {idea.status === 'open' ? 'Close idea' : 'Reopen idea'}
              </Button>
            ),
          } as ColumnsType<Idea>[number],
        ]
      : []),
  ]

  return (
    <Card title="Ideas">
      {error && (
        <Alert
          showIcon
          type="warning"
          message="Could not load ideas"
          description="Please try again."
          style={{ marginBottom: 16 }}
        />
      )}
      <Table
        columns={columns}
        dataSource={ideas}
        loading={loading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: (count) => `${count} ideas`,
        }}
        rowKey="id"
        scroll={{ x: 1100 }}
        onChange={(pagination) => setPage(pagination.current ?? 1)}
      />
    </Card>
  )
}
